import { z } from "zod";
import type { ThirdPartyDeviceType } from "../../DeviceTypes/ThirdPartyDevice.js";
import { ObjectIdString } from "../../validators/index.js";

/**
 * Per-device-type schema for the free-form `deviceTypeSpecificInformation` bag. Every existing device type
 * defaults to this untyped passthrough (today's behaviour, unchanged) until it's given a real schema.
 *
 * `ConfiguringSensorDeviceInfo` (backend's configuringSensorWarningHandler/checks.ts) is a partial precedent
 * for a `draginoSDI12LBLSNode`-specific shape; that fold-in is deliberately deferred, not done here — this
 * registry only needs to exist so a device type introduced later (e.g. WeatherLink) can register a real
 * schema without disturbing the existing passthrough entries.
 */
const passthroughDeviceTypeSpecificInformation = z.record(z.string(), z.any());

/**
 * One WeatherLink sensor discovered for a station, and how it resolves to a sensor cluster.
 * `sensorClusterConfigKey`/`portId`/`channel` are absent when discovery found no matching cluster config
 * (e.g. WLL health, sensor_type 504) — the sensor is recorded so discovery stays visible, not registered.
 */
const weatherLinkSensorMappingSchema = z.object({
    lsid: z.number(),
    sensorType: z.number(),
    // Only WeatherLink's /current archive record carries this, so it's absent until a sensor has
    // reported at least once — discovery leaves it unset rather than guessing.
    dataStructureType: z.number().optional(),
    category: z.string(),
    sensorClusterConfigKey: z.string().optional(),
    portId: z.string().optional(),
    channel: z.number().optional(),
    // The three below are optional because stations discovered before SWD-3371 lack them.
    /** WeatherLink's `created_date` for the sensor: the earliest instant it can have archive data. */
    createdAt: z.coerce.date().nullable().optional(),
    /** WeatherLink's `active` flag for the sensor. */
    active: z.boolean().nullable().optional(),
    /** The ISS transmitter id on the console, `null` for sensors that do not transmit. */
    txId: z.number().nullable().optional(),
});

export type WeatherLinkSensorMapping = z.infer<typeof weatherLinkSensorMappingSchema>;

/**
 * Registration state for a `davisWeatherStation`, as discovery records it. Poll and backfill progress
 * live in the `ingest_cursors` table rather than here: this bag is Mixed, and a document save writes
 * all of it back, which would rewind a cursor a concurrent poll had just advanced (SWD-3347).
 */
const davisWeatherStationDeviceTypeSpecificInformation = z.object({
    gatewayId: z.number().nullable(),
    productNumber: z.string().nullable(),
    timeZone: z.string().nullable(),
    /**
     * The station's height above *sea level*, in feet, exactly as WeatherLink reports it. Recorded for
     * reference only: it is not a `position.depth`, which measures metres below ground level.
     */
    elevation: z.number().nullable(),
    /** The station's archive period in **minutes**, as WeatherLink reports it — never seconds. */
    recordingInterval: z.number().nullable(),
    /** The owning `ThirdPartyApiCredential`'s `recordId` — never its `_id`, which changes every version. */
    credentialRecordId: ObjectIdString,
    sensors: z.array(weatherLinkSensorMappingSchema),
    // Everything below is optional because stations discovered before SWD-3371 lack it. The sync flow
    // reads these to classify a station and to pre-fill its backfill range; none of it drives ingest.
    /**
     * The station's name as WeatherLink has it, kept apart from `name` so a rename in Verdi can be
     * diffed against the source without being overwritten by it.
     */
    sourceName: z.string().optional(),
    /**
     * From `registered_date`. Verified against the live API (2026-09-24): `/historic` returns nothing
     * before this instant, so it is the floor for any backfill. Not an "earliest data" value; the
     * station can have gaps of months after it.
     */
    registeredAt: z.coerce.date().nullable().optional(),
    /** WeatherLink's `active` flag for the station. */
    active: z.boolean().nullable().optional(),
    /** From `subscription_type`, e.g. "Pro". Davis documents that "Basic" has no `/historic` access. */
    subscriptionType: z.string().nullable().optional(),
    /** From `relationship_type`, e.g. "Owned". History on a station shared into the account may be refused. */
    relationshipType: z.string().nullable().optional(),
    /** From `gateway_type`, e.g. "WeatherLink Live". */
    gatewayType: z.string().nullable().optional(),
    /**
     * From `firmware_version`, stringified — WeatherLink's schema allows either a number or a string
     * here, and often omits it altogether at the station level (confirmed against the live API,
     * 2026-09-25: null for a real station). `null` means WeatherLink itself reported nothing, not that
     * this device type has no version concept the way a LoRa device's `deviceVersionInfo` would.
     */
    firmwareVersion: z.string().nullable().optional(),
});

export const deviceTypeSpecificInformationSchemas = {
    sensoterraProbe: passthroughDeviceTypeSpecificInformation,
    draginoSDI12LBLSNode: passthroughDeviceTypeSpecificInformation,
    draginoDL0S8N: passthroughDeviceTypeSpecificInformation,
    rakWirelessRakGateway: passthroughDeviceTypeSpecificInformation,
    ePlantTreeTag: passthroughDeviceTypeSpecificInformation,
    davisWeatherStation: davisWeatherStationDeviceTypeSpecificInformation,
} as const satisfies Record<ThirdPartyDeviceType, z.ZodType>;

export type DeviceTypeSpecificInformationFor<T extends ThirdPartyDeviceType> = z.infer<
    (typeof deviceTypeSpecificInformationSchemas)[T]
>;

/**
 * Typed read of a device's `deviceTypeSpecificInformation` bag, narrowed by its `deviceType`. A single place
 * for future per-device-type schemas to replace the inline `as ...` casts scattered across getters,
 * controllers, and uplink handlers — existing callers are untouched by this change.
 */
export function getDeviceTypeSpecificInformation<T extends ThirdPartyDeviceType>(device: {
    deviceType: T;
    deviceTypeSpecificInformation?: Record<string, any>;
}): DeviceTypeSpecificInformationFor<T> {
    return (device.deviceTypeSpecificInformation ?? {}) as DeviceTypeSpecificInformationFor<T>;
}
